import { config } from './config/generated-config'

export const getJamsBackendApi = () => {
  return config.VITE_JAMS_BACKEND_URL
}

export const getJamsFrontendApi = () => {
  return config.VITE_JAMS_FRONTEND_URL
}

export const getMustApi = () => {
  return config.VITE_MUST_ENDPOINT
}

export const formatPrice = (
  value: string | number = 0,
  options = { showCents: false, showSymbol: true },
): string => {
  const { showCents = true, showSymbol = true } = options

  const valueAsNumber = Number(value)

  // Handle invalid inputs
  if (typeof value === 'string' && isNaN(valueAsNumber)) {
    return value
  }

  // Round to 2 decimal places
  const roundedAmount = Math.round(valueAsNumber * 100) / 100

  // Format with proper grouping and decimal places
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
    currencyDisplay: showSymbol ? 'symbol' : 'code',
  })

  // Remove 'USD' if not showing symbol
  let formatted = formatter.format(roundedAmount)
  if (!showSymbol) {
    formatted = formatted.replace('USD', '').trim()
  }
  return formatted
}

export const formatNumberWithUnit = (
  value: string | number,
  unit: string = '',
  options = {
    decimals: 0,
    spaceBeforeUnit: true,
    unitBefore: false,
    locale: 'en-US',
  },
) => {
  const {
    decimals = 0,
    spaceBeforeUnit = true,
    unitBefore = false,
    locale = 'en-US',
  } = options
  const valueAsNumber = Number(value)

  // Handle invalid inputs
  if (typeof value === 'string' && isNaN(valueAsNumber)) {
    return 'Invalid number'
  }

  // Format number with proper grouping and decimal places
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })

  const formattedNumber = formatter.format(valueAsNumber)

  // Handle unit placement
  if (!unit) return formattedNumber

  if (unitBefore) {
    return `${unit}${spaceBeforeUnit ? ' ' : ''}${formattedNumber}`
  }

  return `${formattedNumber}${spaceBeforeUnit ? ' ' : ''}${unit}`
}

export const formatAccountName = (name: string): string => {
  return name.replace(/_/g, ' ')
}

export const formatPlacementIdToFriendlyName = (id: string) => {
  return id.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
}

export const normalizeDeviceType = (deviceType: string): string => {
  if (!deviceType) return 'unknown'

  const normalized = deviceType.toLowerCase().trim()

  // Keep "unknown" as is for historical data
  if (normalized === 'unknown') {
    return 'unknown'
  }

  // Map common variations to standard types
  if (['mobile', 'phone', 'smartphone', 'cellphone'].includes(normalized)) {
    return 'mobile'
  }

  if (['tablet', 'ipad', 'android-tablet'].includes(normalized)) {
    return 'tablet'
  }

  if (
    ['desktop', 'pc', 'computer', 'laptop', 'mac', 'windows'].includes(
      normalized,
    )
  ) {
    return 'desktop'
  }

  // For any other unrecognized types, map to "unknown" to preserve data
  return 'unknown'
}
